import { useQuery } from "@tanstack/react-query";
import { api, isSessionRejected } from "./api";
import type {
  Item,
  ItemCategory,
  ItemLookup,
  Unit,
  UnitConversion,
  TaxPolicy,
  Supplier,
  Boat,
  Project,
  Site,
  StockLocation,
  StockBalance,
  MeResponse,
  Paginated,
} from "../api-models";

// Reference data used across many pages (dropdowns, hints) — cached for ~5 min
// via TanStack Query, per architecture.md §11.
const REFERENCE_STALE_TIME = 5 * 60 * 1000;

export function useMeQuery(enabled: boolean) {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => (await api.get<MeResponse>("/me")).data,
    enabled,
    staleTime: REFERENCE_STALE_TIME,
    // Retry everything except the server rejecting the session: in local development the
    // API restarts underneath the running app (air rebuilds, and the regenerated
    // api-models.ts makes Vite reload the page), so /me routinely fires into a socket that
    // is refused for a second or two. Without the retry that single failure is read as
    // "not signed in".
    retry: (failureCount, err) => failureCount < 3 && !isSessionRejected(err),
  });
}

// The first page of active items only: the server caps a page at 200 and the master runs
// to thousands, so this is not the whole master and a picker built on it cannot offer or
// even name anything past the cap. New pickers use components/ItemSelect.tsx, which
// searches the master on the server; the pages still on this hook are due the same move.
export function useItemsQuery() {
  return useQuery({
    queryKey: ["items", "all"],
    queryFn: async () => (await api.get<Paginated<Item>>("/items", { params: { pageSize: 200, active: true } })).data.data,
    staleTime: REFERENCE_STALE_TIME,
  });
}

// Every material in the master, whatever its status, in the slim lookup shape. The
// spreadsheet importers match a whole sheet of codes in the browser, so they need all of
// it - /items is paginated and stops at 200, which made every code past the first page
// read as unknown. Inactive materials are included because the server's imports resolve
// them too: an inactive code is still an existing record, and the importer has to say so.
export function useItemLookupQuery() {
  return useQuery({
    queryKey: ["items", "lookup"],
    queryFn: async () => (await api.get<ItemLookup[]>("/items/lookup")).data,
    staleTime: REFERENCE_STALE_TIME,
  });
}

export function useItemCategoriesQuery() {
  return useQuery({
    queryKey: ["item-categories"],
    queryFn: async () => (await api.get<ItemCategory[]>("/item-categories")).data,
    staleTime: REFERENCE_STALE_TIME,
  });
}

export function useUnitsQuery() {
  return useQuery({
    queryKey: ["units"],
    queryFn: async () => (await api.get<Unit[]>("/units")).data,
    staleTime: REFERENCE_STALE_TIME,
  });
}

export function useUnitConversionsQuery() {
  return useQuery({
    queryKey: ["unit-conversions"],
    queryFn: async () => (await api.get<UnitConversion[]>("/unit-conversions")).data,
    staleTime: REFERENCE_STALE_TIME,
  });
}

// The tax rates a material master and a purchase order line can be assigned. Ordered by
// rate on the server, which is how they read in a picker.
export function useTaxPoliciesQuery() {
  return useQuery({
    queryKey: ["tax-policies"],
    queryFn: async () => (await api.get<TaxPolicy[]>("/tax-policies")).data,
    staleTime: REFERENCE_STALE_TIME,
  });
}

export function useSuppliersQuery() {
  return useQuery({
    queryKey: ["suppliers", "all"],
    queryFn: async () => (await api.get<Paginated<Supplier>>("/suppliers", { params: { pageSize: 1000, active: true } })).data.data,
    staleTime: REFERENCE_STALE_TIME,
  });
}

// The vessel register behind Boat Notes. Retired boats are left out: a manifest can only
// be booked against a boat still in service (boat_note.go's resolveBoat).
export function useBoatsQuery() {
  return useQuery({
    queryKey: ["boats", "active"],
    queryFn: async () => (await api.get<Paginated<Boat>>("/boats", { params: { pageSize: 1000, active: true } })).data.data,
    staleTime: REFERENCE_STALE_TIME,
  });
}

export function useProjectsQuery() {
  return useQuery({
    queryKey: ["projects", "all"],
    queryFn: async () => (await api.get<Paginated<Project>>("/projects", { params: { pageSize: 1000 } })).data.data,
    staleTime: REFERENCE_STALE_TIME,
  });
}

export function useSitesQuery(projectId?: number) {
  return useQuery({
    queryKey: ["sites", "all", projectId ?? null],
    queryFn: async () =>
      (await api.get<Paginated<Site>>("/sites", { params: { pageSize: 1000, projectId } })).data.data,
    staleTime: REFERENCE_STALE_TIME,
  });
}

export function useStockLocationsQuery() {
  return useQuery({
    queryKey: ["stock-locations", "all"],
    queryFn: async () => (await api.get<StockLocation[]>("/stock-locations")).data,
    staleTime: REFERENCE_STALE_TIME,
  });
}

// The quarantine stores hanging off each Central/Site location. Kept out of
// useStockLocationsQuery so ordinary destination pickers never offer a store nobody is
// meant to move stock into directly - only the quarantine dispositions list these.
export function useQuarantineLocationsQuery(enabled = true) {
  return useQuery({
    queryKey: ["stock-locations", "quarantine"],
    queryFn: async () =>
      (await api.get<StockLocation[]>("/stock-locations", { params: { type: "QUARANTINE" } })).data,
    enabled,
    staleTime: REFERENCE_STALE_TIME,
  });
}

/** A site's primary store — the one auto-created with the site, mirroring siteLocationID() on the server. */
export function useSiteLocationId(siteId?: number) {
  const { data: locations = [] } = useStockLocationsQuery();
  if (!siteId) return undefined;
  return locations
    .filter((l) => l.type === "SITE" && l.siteId === siteId)
    .sort((a, b) => a.id - b.id)[0]?.id;
}

// Balances at a site's own store. Not reference data — it moves with every posting, so it
// is kept far fresher than the 5-min dropdown caches. Returns undefined while unresolved
// (no site yet, still loading, or no Stock.Read on that site) so callers can tell "nothing
// in stock" apart from "we don't know".
export function useSiteStockQuery(siteId?: number) {
  const locationId = useSiteLocationId(siteId);
  return useQuery({
    queryKey: ["stock", "site-store", locationId ?? null],
    queryFn: async () =>
      (await api.get<Paginated<StockBalance>>("/stock", { params: { locationId, pageSize: 1000 } })).data.data,
    enabled: !!locationId,
    staleTime: 30 * 1000,
    retry: false,
  });
}
